import { DownOutlined } from '@ant-design/icons';
import { Bar, BarConfig } from '@ant-design/plots';
import { useLazyQuery } from '@apollo/client';
import { GET_IDEA_BUSINESS_AREA } from '@frontend/data-access';
import { Button, Card, Dropdown, Select } from 'antd';
import useTranslation from 'next-translate/useTranslation';
import { useEffect, useMemo, useState } from 'react';

function IdeaAreaStats({ className }: { className?: string }) {
  const { t } = useTranslation('admin');
  const [data, setData] = useState<{ name: string; count: number }[]>([]);

  const [sortOption, setSortOption] = useState({
    key: 'HL',
    label: t('highest_to_lowest'),
  });
  const [filter, setFilter] = useState<string[]>([
    'DRAFT',
    'PUBLISHED',
    'HIDDEN',
    'REPORTED',
  ]);
  const [areaFilter, setAreaFilter] = useState<string[]>([]);
  const [getIdeaAreas, { loading }] = useLazyQuery<{
    dashboardTopIdeaAreas: { name: string; count: number }[];
  }>(GET_IDEA_BUSINESS_AREA, {
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      setData(data.dashboardTopIdeaAreas);
    },
  });

  const sortOptions = useMemo(
    () => [
      { key: 'HL', label: t('highest_to_lowest') },
      { key: 'LH', label: t('lowest_to_highest') },
    ],
    [t]
  );

  const filterOptions = useMemo(
    () => [
      { label: t('drafted'), value: 'DRAFT' },
      { label: t('published'), value: 'PUBLISHED' },
      { label: t('hidden'), value: 'HIDDEN' },
      { label: t('reported'), value: 'REPORTED' },
    ],
    [t]
  );

  useEffect(() => {
    const sortedData = [...data].sort((a, b) => {
      if (sortOption.key === 'HL') {
        return b.count - a.count;
      } else {
        return a.count - b.count;
      }
    });
    setData(sortedData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortOption]);

  const config: BarConfig = {
    data: data,
    xField: 'name',
    yField: 'count',
    color: '#2888ED',
    paddingRight: 20,
    label: {
      text: 'count',
      position: 'right',
      textAlign: 'start',
      dx: 8,
      style: {
        fontFamily: 'Lufga',
      },
    },
    yAxis: {
      label: {
        style: {
          fontFamily: 'Lufga',
          fill: 'rgba(0, 0, 0, 0.65)',
          textAlign: 'right',
        },
        labelFormatter: (value) => {
          const str = value.toString();
          if (str.length > 20) {
            return str.slice(0, 20) + '...';
          }
          return str;
        },
      },
    },
  };

  const handleFilterChange = (value: string[]) => {
    setFilter(value);
  };

  useEffect(() => {
    getIdeaAreas({
      variables: {
        statuses: filter,
      },
    });
  }, [filter, getIdeaAreas]);

  return (
    <Card
      style={{
        borderRadius: 20,
      }}
      className={`${className}`}
    >
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <div className="text-sm text-gray-900 font-medium">
            {t('idea_by_business_functional_area')}
          </div>
          <div>
            <Dropdown
              menu={{
                items: sortOptions.map((item) => ({
                  ...item,
                  onClick: () => setSortOption(item),
                })),
              }}
              placement="bottomRight"
              trigger={['click']}
            >
              <Button shape="round">
                {sortOption.label}
                <DownOutlined />
              </Button>
            </Dropdown>
          </div>
        </div>
        <div className="flex space-x-3">
          <Select
            onChange={handleFilterChange}
            options={filterOptions}
            value={filter}
            placeholder=""
            allowClear
            mode="multiple"
            optionFilterProp="label"
            style={{ width: '100%' }}
            maxTagCount="responsive"
          />
          <Select
            onChange={(value: string[]) => setAreaFilter(value)}
            options={data?.map((item) => ({
              label: item.name,
              value: item.name,
            }))}
            value={areaFilter}
            placeholder={t('filter_by_business_functional_area')}
            allowClear
            mode="multiple"
            optionFilterProp="label"
            style={{ width: '100%' }}
            maxTagCount="responsive"
          />
        </div>
        <Bar
          loading={loading}
          {...config}
          data={data?.filter(
            (item) => areaFilter.length === 0 || areaFilter.includes(item?.name)
          )}
        />
      </div>
    </Card>
  );
}

export default IdeaAreaStats;
